import {
  Box,
  Divider,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Textarea,
  Tooltip,
  useDisclosure,
  Menu as ChakraMenu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { Fragment, useCallback, useState } from 'react';
import { Interface, FunctionFragment, ParamType } from 'ethers';
import { ReactComponent as InfoIcon } from 'assets/info-icon.svg';
import { ReactComponent as AddressBookIcon } from 'assets/address-book-icon.svg';
import { EXAMPLE_ABI } from 'utils/constants';
import { AbiMethodTypes } from 'types/abi';
import AbiInput from 'components/ABI';
import { ContractInteractionData } from '../types';
import AddressBookPickerModal from 'components/Modals/AddressBookPickerModal';

interface ContractInteractionFormProps {
  data: ContractInteractionData;
  onChange: (data: ContractInteractionData) => void;
}

interface ParsedFunction {
  name: string;
  inputs: Array<{ name: string; type: string; value?: any }>;
  fragment: FunctionFragment;
}

const dropdownButtonStyle = {
  border: '1px solid',
  borderColor: 'inherit',
  cursor: 'pointer',
  bg: 'white',
  transition: 'all 0.15s',
  _hover: { borderColor: 'blackAlpha.400' },
};

const ContractInteractionForm = ({ data, onChange }: ContractInteractionFormProps) => {
  const [formatError, setFormatError] = useState(false);
  const [ethersInterface, setEthersInterface] = useState<Interface | null>(null);
  const [contractFunctions, setContractFunctions] = useState<Record<string, ParsedFunction>>({});
  const [functionNames, setFunctionNames] = useState<string[]>([]);
  const { isOpen: isPickerOpen, onOpen: onPickerOpen, onClose: onPickerClose } = useDisclosure();

  const mapSolidityTypeToAbiType = (solidityType: string): string => {
    if (solidityType === 'address') return AbiMethodTypes.Address;
    if (solidityType === 'bool') return AbiMethodTypes.Bool;
    if (solidityType === 'string') return AbiMethodTypes.String;
    if (solidityType.startsWith('uint')) return AbiMethodTypes.Uint256;
    if (solidityType.startsWith('int')) return AbiMethodTypes.Int256;
    return solidityType;
  };

  const parseAbi = useCallback((abiJson: any[], currentAbi: string, currentData: ContractInteractionData) => {
    try {
      const iface = new Interface(abiJson);
      setEthersInterface(iface);

      const functions: Record<string, ParsedFunction> = {};
      const names: string[] = [];

      iface.forEachFunction((fragment) => {
        if (fragment.stateMutability !== 'view' && fragment.stateMutability !== 'pure') {
          const inputs = fragment.inputs.map((input: ParamType) => {
            const mappedType = mapSolidityTypeToAbiType(input.type);
            const isString = mappedType === AbiMethodTypes.String || input.type === 'string';
            return {
              name: input.name || 'unnamed',
              type: mappedType,
              value: isString ? '' : undefined,
            };
          });

          names.push(fragment.name);
          functions[fragment.name] = {
            name: fragment.name,
            inputs,
            fragment,
          };
        }
      });

      if (names.length === 0) {
        setFormatError(true);
        return;
      }

      setContractFunctions(functions);
      setFunctionNames(names);
      setFormatError(false);

      // Auto-select first method if none selected
      if (!currentData.selectedMethod || !functions[currentData.selectedMethod]) {
        onChange({
          ...currentData,
          abi: currentAbi,
          selectedMethod: names[0],
          methodInputs: functions[names[0]].inputs.map(input => ({
            ...input,
            value: input.value,
          })),
        });
      }
    } catch (e) {
      console.error('Error parsing ABI:', e);
      setFormatError(true);
      setEthersInterface(null);
      setContractFunctions({});
      setFunctionNames([]);
    }
  }, [onChange]);

  const handleAbiChange = (value: string) => {
    onChange({ ...data, abi: value });
    try {
      const parsed = JSON.parse(value);
      parseAbi(parsed, value, data);
    } catch {
      setFormatError(true);
      setContractFunctions({});
      setFunctionNames([]);
    }
  };

  const handleMethodChange = (methodName: string) => {
    const func = contractFunctions[methodName];
    if (func) {
      onChange({
        ...data,
        selectedMethod: methodName,
        methodInputs: func.inputs.map(input => ({
          ...input,
          value: (input.type === AbiMethodTypes.String || input.type === 'string') ? '' : undefined,
        })),
      });
    }
  };

  const handleInputChange = (index: number, value: any) => {
    const updatedInputs = [...data.methodInputs];
    updatedInputs[index].value = value;
    onChange({ ...data, methodInputs: updatedInputs });
  };

  return (
    <Flex direction="column" gap={4} w="full">
      <Box>
        <Text mb={2} fontWeight={500}>Contract Address (Hedera ID)</Text>
        <InputGroup>
          <Input
            h={12}
            borderRadius={16}
            placeholder="0.0.xxxxx"
            value={data.contractId}
            onChange={(e) => onChange({ ...data, contractId: e.target.value })}
          />
          <InputRightElement h={12} w={12}>
            <Box
              title="Select from Address Book"
              cursor="pointer"
              fontSize="18px"
              onClick={onPickerOpen}
            >
              <AddressBookIcon />
            </Box>
          </InputRightElement>
        </InputGroup>
      </Box>

      <Box>
        <Text mb={2} fontWeight={500}>Description (optional)</Text>
        <Input
          h={12}
          borderRadius={16}
          placeholder="Brief description of this contract call"
          value={data.description}
          onChange={(e) => onChange({ ...data, description: e.target.value })}
        />
      </Box>

      <Box>
        <Flex justify="space-between" align="center" mb={2}>
          <Text fontWeight={500}>Contract ABI</Text>
          <Tooltip
            bg="primary"
            placement="right"
            borderRadius={12}
            fontSize={10}
            label={
              <pre>
                <Text textAlign="center" p={2} m={2} borderTopRadius={12} bg="secondary" fontSize={10}>
                  EXAMPLE ABI
                </Text>
                <Text color="black" m={5} fontSize={10}>
                  {EXAMPLE_ABI}
                </Text>
              </pre>
            }
          >
            <InfoIcon cursor="pointer" />
          </Tooltip>
        </Flex>
        <Textarea
          value={data.abi}
          borderRadius={16}
          isInvalid={formatError}
          rows={5}
          sx={{
            height: '120px',
            minHeight: '80px',
            maxHeight: '200px',
            overflowY: 'auto !important',
            resize: 'vertical',
          }}
          onChange={(e) => handleAbiChange(e.target.value)}
        />
        {formatError && (
          <Text color="red.500" size="xs" mt={1}>
            Invalid ABI data
          </Text>
        )}
      </Box>

      <Flex gap={4}>
        <Box flex={1}>
          <Text mb={2} fontWeight={500}>Gas Limit</Text>
          <Input
            h={12}
            borderRadius={16}
            type="number"
            placeholder="100000"
            value={data.gas}
            onChange={(e) => onChange({ ...data, gas: e.target.value })}
          />
        </Box>
        <Box flex={1}>
          <Flex align="center" gap={1} mb={2}>
            <Text fontWeight={500}>Payable Amount</Text>
            <Tooltip
              label="Amount of HBAR to send with the contract call (for payable functions)"
              placement="top"
              borderRadius={8}
            >
              <InfoIcon cursor="pointer" />
            </Tooltip>
          </Flex>
          <Input
            h={12}
            borderRadius={16}
            type="number"
            step="0.01"
            placeholder="0"
            value={data.payableAmount}
            onChange={(e) => onChange({ ...data, payableAmount: e.target.value })}
          />
        </Box>
      </Flex>

      {functionNames.length > 0 && (
        <>
          <Box>
            <Text mb={2} fontWeight={500}>Contract Method</Text>
            <ChakraMenu placement="bottom-start" matchWidth>
              <MenuButton
                as={Box}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                h={12}
                px={4}
                borderRadius={16}
                {...dropdownButtonStyle}
              >
                <Text fontSize="sm" fontWeight="500">
                  {data.selectedMethod || 'Select method'}
                </Text>
              </MenuButton>
              <MenuList py={2} borderRadius={12} boxShadow="lg" zIndex={10}>
                {functionNames.map((name, idx) => (
                  <MenuItem
                    key={idx}
                    fontSize="sm"
                    py={2}
                    fontWeight={data.selectedMethod === name ? '600' : '400'}
                    bg={data.selectedMethod === name ? 'blackAlpha.50' : 'transparent'}
                    _hover={{ bg: 'blackAlpha.100' }}
                    onClick={() => handleMethodChange(name)}
                  >
                    {name}
                  </MenuItem>
                ))}
              </MenuList>
            </ChakraMenu>
          </Box>

          {data.methodInputs.length > 0 && (
            <Fragment>
              <Divider />
              <Text fontWeight={500}>Function Parameters</Text>
              <Flex direction="column" gap={3}>
                {data.methodInputs.map((input, index) => (
                  <AbiInput
                    key={`${input.type}-${index}`}
                    abiProps={{
                      selectedMethodInputs: data.methodInputs,
                      input,
                      index,
                      setter: handleInputChange,
                    }}
                  />
                ))}
              </Flex>
            </Fragment>
          )}
        </>
      )}

      <AddressBookPickerModal
        isOpen={isPickerOpen}
        onClose={onPickerClose}
        onSelect={(address) => onChange({ ...data, contractId: address })}
      />
    </Flex>
  );
};

export default ContractInteractionForm;
